import { DefaultChatTransport } from 'ai';
import type { ChatTransport } from 'ai';

import { chatBackendApi, NOVA_V2_CHAT_BACKEND, type NovaChatBackend } from './chat-backend';
import { NovaWorkflowChatTransport } from './durable-transport';
import { startNovaTurnTelemetry } from './nova-turn-telemetry';
import type { UIMessageWithMetadata } from './types';
import { getToolHandoffPart } from '../../../lib/tool-handoff';

interface CreateTransportOptions {
  backend: NovaChatBackend;
  getToken: () => Promise<string | undefined>;
  onReconnectUnavailable?: (chatUuid: string) => void;
  onWorkflowAttached?: (attachment: { chatUuid: string; workflowRunId: string }) => void;
  userUuid: string;
  accountUuid?: string;
}

export type NovaChatTransport = ChatTransport<UIMessageWithMetadata> & {
  abortActiveReconnects?: () => void;
  stopActiveWorkflow?: (request: { chatUuid: string; workflowRunId?: string }) => Promise<void>;
};

function withNovaTurnTelemetry(transport: NovaChatTransport, path: 'durable' | 'non_durable'): NovaChatTransport {
  const stopActiveWorkflow = transport.stopActiveWorkflow?.bind(transport);

  return {
    sendMessages: (request) => {
      startNovaTurnTelemetry({
        chatUuid: request.chatId,
        messages: request.messages,
        path,
        trigger: request.trigger,
      });
      return transport.sendMessages(request);
    },
    reconnectToStream: (request) => transport.reconnectToStream(request),
    ...(transport.abortActiveReconnects ? { abortActiveReconnects: () => transport.abortActiveReconnects?.() } : {}),
    ...(stopActiveWorkflow ? { stopActiveWorkflow } : {}),
  };
}

interface NovaPrepareSendMessagesRequest {
  id: string;
  messages: UIMessageWithMetadata[];
  body?: Record<string, unknown>;
  headers?: HeadersInit;
}

interface NovaRegenerationRequest {
  feedbackUuid: string;
  targetMessageUuid: string;
}

function normalizeChatUuid(id: string) {
  return id.replace('new-chat-', '');
}

export function buildNovaReconnectToStreamApiUrl({
  api,
  chatUuid,
  startIndex,
  workflowRunId,
}: {
  api: string;
  chatUuid: string;
  startIndex?: number;
  workflowRunId?: string;
}) {
  const url = `${api.replace(/\/$/, '')}/${normalizeChatUuid(chatUuid)}/stream`;
  const searchParams = new URLSearchParams();

  if (startIndex !== undefined) {
    searchParams.set('startIndex', String(startIndex));
  }
  if (workflowRunId !== undefined) {
    searchParams.set('workflowRunId', workflowRunId);
  }

  const query = searchParams.toString();
  return query ? `${url}?${query}` : url;
}

export function buildNovaStopActiveWorkflowApiUrl({
  api,
  chatUuid,
  workflowRunId,
}: {
  api: string;
  chatUuid: string;
  workflowRunId?: string;
}) {
  const url = `${api.replace(/\/$/, '')}/${normalizeChatUuid(chatUuid)}/stop`;

  if (!workflowRunId) {
    return url;
  }

  const searchParams = new URLSearchParams({ workflowRunId });
  return `${url}?${searchParams.toString()}`;
}

export function isDurableActiveStreamAlreadyPersisted({
  activeStreamId,
  messages,
}: {
  activeStreamId?: string | null;
  messages: readonly UIMessageWithMetadata[];
}): boolean {
  const latestMessage = messages.at(-1);

  return (
    typeof activeStreamId === 'string' &&
    activeStreamId.length > 0 &&
    latestMessage?.role === 'assistant' &&
    latestMessage.metadata?.turnWorkflowId === activeStreamId
  );
}

/**
 * A durable turn claims `activeStreamId` before its message base is persisted. Normal turns are
 * safe to resume once the exact triggering user message, stamped with that workflow id, is
 * visible. Tool handoff continuations target an existing assistant message, so they are safe once
 * that exact pending-or-resolved handoff message is visible. The continuation stream's first
 * canonical chunk resolves a pending tool part in place before appending model output.
 */
export function isDurableActiveStreamBaseHydrated({
  activeStreamId,
  chatUuid,
  messages,
}: {
  activeStreamId?: string | null;
  chatUuid: string;
  messages: readonly UIMessageWithMetadata[];
}): boolean {
  if (!activeStreamId) {
    return true;
  }

  const [workflowKind, workflowChatUuid, targetMessageUuid] = activeStreamId.split('/');
  if (
    workflowKind !== 'nova-chat' ||
    normalizeChatUuid(workflowChatUuid ?? '') !== normalizeChatUuid(chatUuid) ||
    !targetMessageUuid
  ) {
    return false;
  }

  const latestMessage = messages.at(-1);
  if (latestMessage?.role === 'user') {
    return latestMessage.metadata?.turnWorkflowId === activeStreamId;
  }

  if (latestMessage?.role !== 'assistant' || latestMessage.id !== targetMessageUuid) {
    return false;
  }

  const handoffPart = getToolHandoffPart(latestMessage);
  return !handoffPart || handoffPart.state === 'input-available' || handoffPart.state === 'output-available';
}

/**
 * Reconciles a server marker for a turn whose canonical assistant is already persisted.
 * The exact GET lets the backend compare-clear a terminal/stale activeStreamId, but its
 * replay body is cancelled before any chunk can enter useChat.
 */
export async function probePersistedDurableActiveStream({
  chatUuid,
  fetchImplementation = globalThis.fetch,
  getToken,
  signal,
  workflowRunId,
}: {
  chatUuid: string;
  fetchImplementation?: typeof fetch;
  getToken: () => Promise<string | undefined>;
  signal?: AbortSignal;
  workflowRunId: string;
}): Promise<void> {
  const token = await getToken();
  const response = await fetchImplementation(
    buildNovaReconnectToStreamApiUrl({
      api: chatBackendApi(NOVA_V2_CHAT_BACKEND),
      chatUuid,
      startIndex: 0,
      workflowRunId,
    }),
    {
      method: 'GET',
      headers: {
        authorization: `Bearer ${token}`,
      },
      signal,
    },
  );

  if (!response.ok) {
    throw new Error((await response.text()) || `Failed to reconcile Nova durable stream (${response.status})`);
  }

  await response.body?.cancel();
}

function getChatUuidFromReconnectUrl(url: RequestInfo | URL): string | undefined {
  const urlString = typeof url === 'string' ? url : url instanceof URL ? url.href : url.url;
  const match = urlString.match(/\/chat\/completions\/([^/?#]+)\/stream(?:[?#].*)?$/);

  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function splitRequestBody(body?: Record<string, unknown>) {
  const { additionalContext, ...restBodyData } = body || {};

  const flattenedAdditionalContext =
    additionalContext && typeof additionalContext === 'object' ? (additionalContext as Record<string, unknown>) : {};

  return {
    flattenedAdditionalContext,
    restBodyData,
  };
}

function parseRegenerationRequest(value: unknown): NovaRegenerationRequest | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

  const { feedbackUuid, targetMessageUuid } = value as Record<string, unknown>;
  if (
    typeof feedbackUuid !== 'string' ||
    feedbackUuid.length === 0 ||
    typeof targetMessageUuid !== 'string' ||
    targetMessageUuid.length === 0
  ) {
    return undefined;
  }

  return { feedbackUuid, targetMessageUuid };
}

// createdAt is a provisional client-side stamp. Keeping it out of the request body prevents it from
// being persisted into the backend metadata blob, where a stale copy could shadow the authoritative
// createdAt column on hydration.
function stripClientOnlyMetadata(metadata: UIMessageWithMetadata['metadata']): UIMessageWithMetadata['metadata'] {
  if (metadata?.createdAt === undefined) {
    return metadata;
  }
  const { createdAt, ...rest } = metadata;
  return rest;
}

function buildAssistantBody({
  accountUuid,
  chatUuid,
  lastMessage,
  requestBody,
  userUuid,
}: {
  accountUuid?: string;
  chatUuid: string;
  lastMessage: UIMessageWithMetadata;
  requestBody?: Record<string, unknown>;
  userUuid: string;
}) {
  const { flattenedAdditionalContext, restBodyData } = splitRequestBody(requestBody);

  return {
    userUuid,
    accountUuid,
    chatUuid,
    messageUuid: requestBody?.messageUuid,
    role: lastMessage.role,
    parts: lastMessage.parts,
    metadata: stripClientOnlyMetadata(lastMessage.metadata),
    ...flattenedAdditionalContext,
    ...restBodyData,
  };
}

export function buildNovaRequestBody({
  accountUuid,
  request,
  userUuid,
}: {
  accountUuid?: string;
  request: NovaPrepareSendMessagesRequest;
  userUuid: string;
}) {
  const chatUuid = normalizeChatUuid(request.id);
  const rawRegeneration = request.body?.regeneration;
  const regeneration = parseRegenerationRequest(rawRegeneration);
  if (rawRegeneration !== undefined && !regeneration) {
    throw new Error('Invalid Nova regeneration request');
  }
  if (regeneration) {
    const { flattenedAdditionalContext, restBodyData } = splitRequestBody(request.body);
    const requestContext = Object.fromEntries(
      Object.entries(restBodyData).filter(([key]) => key !== 'accountUuid' && key !== 'regeneration'),
    );

    // Regeneration identifies the persisted assistant answer directly, so replaying
    // the latest user message would create a second turn instead of a new variant.
    return {
      ...flattenedAdditionalContext,
      ...requestContext,
      userUuid,
      accountUuid,
      chatUuid,
      regeneration,
    };
  }

  const lastMessage = request.messages.at(-1);

  if (!lastMessage) {
    throw new Error('No last message found');
  }

  if (lastMessage.role === 'assistant') {
    const handoffPart = getToolHandoffPart(lastMessage);

    if (handoffPart?.state === 'output-available') {
      const attachmentUploads = request.body?.attachmentUploads;
      return {
        userUuid,
        chatUuid,
        messageUuid: lastMessage.id,
        parts: [handoffPart],
        ...(Array.isArray(attachmentUploads) && attachmentUploads.length > 0 ? { attachmentUploads } : {}),
      };
    }

    return buildAssistantBody({
      accountUuid,
      chatUuid,
      lastMessage,
      requestBody: request.body,
      userUuid,
    });
  }

  const { flattenedAdditionalContext, restBodyData } = splitRequestBody(request.body);

  return {
    ...flattenedAdditionalContext,
    ...restBodyData,
    userUuid,
    accountUuid,
    chatUuid,
    parts: lastMessage.parts,
    metadata: stripClientOnlyMetadata(lastMessage.metadata),
  };
}

/**
 * Creates the chat transport for a backend.
 * Handles authentication, request body preparation, and error handling.
 */
export function createNovaTransport({
  backend,
  getToken,
  onReconnectUnavailable,
  onWorkflowAttached,
  userUuid,
  accountUuid,
}: CreateTransportOptions): NovaChatTransport {
  const api = chatBackendApi(backend);

  const prepareSendMessagesRequest = (request: NovaPrepareSendMessagesRequest) => {
    // Prefer the per-request accountUuid: the transport instance can outlive
    // an account switch (the AI SDK only swaps transports when the chat id
    // changes), so the closed-over value may be stale.
    const effectiveAccountUuid = (request.body?.accountUuid as string | undefined) || accountUuid;

    return {
      body: buildNovaRequestBody({
        accountUuid: effectiveAccountUuid,
        request,
        userUuid,
      }),
      headers: request.headers,
    };
  };

  if (backend.kind === 'nova-v2') {
    const transport = new NovaWorkflowChatTransport({
      api,
      getReconnectApi: (chatId) => buildNovaReconnectToStreamApiUrl({ api, chatUuid: chatId }),
      getStopApi: (chatId, workflowRunId) =>
        buildNovaStopActiveWorkflowApiUrl({ api, chatUuid: chatId, workflowRunId }),
      getToken,
      onReconnectUnavailable,
      onWorkflowAttached,
      prepareSendMessagesRequest,
    });
    return withNovaTurnTelemetry(transport, 'durable');
  }

  const transport = new DefaultChatTransport<UIMessageWithMetadata>({
    api,
    fetch: async (url: RequestInfo | URL, options?: RequestInit) => {
      const token = await getToken();

      const response = await fetch(url, {
        ...options,
        headers: {
          ...options?.headers,
          authorization: `Bearer ${token}`,
        },
      });

      if (options?.method === 'GET' && response.status === 204) {
        const reconnectChatUuid = getChatUuidFromReconnectUrl(url);
        if (reconnectChatUuid) {
          onReconnectUnavailable?.(reconnectChatUuid);
        }
      }

      return response;
    },
    prepareReconnectToStreamRequest: ({ id }) => ({
      api: buildNovaReconnectToStreamApiUrl({ api, chatUuid: id }),
    }),
    prepareSendMessagesRequest,
  });
  return withNovaTurnTelemetry(transport, 'non_durable');
}
